import { useNavigate } from 'react-router-dom';

import { useCurrentUser, useLogout } from '../../lib/authStore';
import { Dropdown, type DropdownOption } from '../ui/Dropdown';

import { Ic } from './icons';

function initials(name: string | null | undefined, email?: string | null): string {
  const base = (name ?? email ?? '?').trim();
  const parts = base.split(/\s+/).filter(Boolean);
  const first = parts[0] ?? base;
  const second = parts[1];
  if (second !== undefined) return (first.slice(0, 1) + second.slice(0, 1)).toUpperCase();
  return first.slice(0, 2).toUpperCase();
}

const USER_MENU_OPTIONS: readonly DropdownOption<string>[] = [
  { value: 'settings', label: 'Settings', icon: <Ic.gear width={16} height={16} /> },
  { value: 'logout', label: 'Log out', icon: <Ic.logout width={16} height={16} />, danger: true },
];

/**
 * Top-right account button: an avatar that opens the shared dropdown with
 * Settings + Log out.
 *
 * Settings NAVIGATES to `/settings` — it used to open a modal this component
 * owned, along with the module section that modal composed. Making it a real
 * page means the shell has ONE settings destination rather than a rail entry
 * and a popup, and the composition moved to `pages/SettingsPage.tsx`, which is
 * the layer allowed to know about country modules.
 */
export function UserMenu(): JSX.Element | null {
  const user = useCurrentUser();
  const logout = useLogout();
  const navigate = useNavigate();

  if (!user) return null;

  function handle(action: string): void {
    if (action === 'settings') {
      navigate('/settings');
    } else if (action === 'logout') {
      logout();
      navigate('/login', { replace: true });
    }
  }

  return (
    <>
      <Dropdown
        value=""
        align="end"
        buttonClassName="crm-usermenu-trigger"
        options={USER_MENU_OPTIONS}
        onChange={handle}
        menuHeader={
          <>
            <span className="ui-dropdown-menu-head-name">{user.displayName}</span>
            {user.email !== undefined && (
              <span className="ui-dropdown-menu-head-mail">{user.email}</span>
            )}
          </>
        }
        trigger={() => (
          <span
            className="crm-usermenu-avatar"
            role="img"
            aria-label={`Signed in as ${user.displayName}`}
          >
            {initials(user.displayName, user.email)}
          </span>
        )}
      />
    </>
  );
}
